import { useState } from "react";
import type { PlayerView, Resource, ResourceLocation, ResourcePlan } from "../types";
import { resourceMeta } from "../gameMeta";
import {
  useResourceBoardPicker,
  type ResourceInteractionMode,
  type ResourceSlot,
} from "./BoardInteraction";
import { ResourceAmounts, ResourceIcon } from "./ResourceIcon";

type Mode = ResourceInteractionMode;
type Slot = ResourceSlot;

export interface LocationOption {
  key: string;
  location: ResourceLocation;
  resource: Resource;
  amount: number;
  capacity: number;
  label: string;
}

export interface ResourcePlanRequest {
  payment: Resource[];
  storage: Resource[];
  requireStorage: boolean;
  title: string;
}

export interface ResourcePlanConfirmation {
  eyebrow: string;
  title: string;
  description: string;
  confirmLabel: string;
}

export function resourceLocations(player: PlayerView, incoming: ResourceLocation[] = []): LocationOption[] {
  const regions = [...player.principality.upper_regions, ...player.principality.lower_regions];
  const incomingCount = (location: ResourceLocation) => incoming.filter((candidate) => locationKey(candidate) === locationKey(location)).length;
  const options: LocationOption[] = regions.map((region) => {
    const location = { kind: "region" as const, region_id: region.region_id };
    const amount = region.amount + incomingCount(location);
    return {
      key: `r:${region.region_id}`,
      location,
      resource: region.resource,
      amount,
      capacity: 3 - region.amount,
      label: `${resourceMeta[region.resource].label} ${region.production_number} · ${amount}/3`,
    };
  });
  for (const region of regions) {
    const mine = region.expansion;
    if (mine?.instance.card_id === "gold.verborgen_goudmijn") {
      const location = { kind: "hidden_gold_mine" as const, instance_id: mine.instance.instance_id };
      const amount = mine.counter + incomingCount(location);
      options.push({
        key: `m:${mine.instance.instance_id}`,
        location,
        resource: "goud",
        amount,
        capacity: 3 - mine.counter,
        label: `Verborgen goudmijn · ${amount}/3`,
      });
    }
  }
  return options;
}

export function resourceTotal(player: PlayerView, resource: Resource, mode: Mode): number {
  return resourceLocations(player)
    .filter((option) => option.resource === resource)
    .reduce((total, option) => total + (mode === "payment" ? option.amount : option.capacity), 0);
}

export function allocateResourceSlots(player: PlayerView, resources: Resource[], mode: Mode, incoming: ResourceLocation[] = []): Slot[] {
  const options = resourceLocations(player, mode === "payment" ? incoming : []);
  const remaining = new Map(options.map((option) => [option.key, mode === "payment" ? option.amount : option.capacity]));
  return resources.map((resource) => {
    const option = options.find((candidate) => candidate.resource === resource && (remaining.get(candidate.key) || 0) > 0);
    if (!option) return null;
    remaining.set(option.key, (remaining.get(option.key) || 0) - 1);
    return option.location;
  });
}

export function resourceChoiceExists(player: PlayerView, resources: Resource[], mode: Mode): boolean {
  return [...new Set(resources)].some((resource) => (
    resourceLocations(player).filter((option) => option.resource === resource && (mode === "payment" ? option.amount : option.capacity) > 0).length > 1
  ));
}

export function locationKey(location: ResourceLocation): string {
  return location.kind === "region" ? `r:${location.region_id}` : `m:${location.instance_id}`;
}

export function slotsAreValid(player: PlayerView, resources: Resource[], slots: Slot[], mode: Mode, requireAll: boolean, incoming: ResourceLocation[] = []): boolean {
  if (slots.length !== resources.length || (requireAll && slots.some((slot) => !slot))) return false;
  const options = new Map(resourceLocations(player, mode === "payment" ? incoming : []).map((option) => [option.key, option]));
  const usage = new Map<string, number>();
  for (let index = 0; index < slots.length; index += 1) {
    const slot = slots[index];
    if (!slot) continue;
    const key = locationKey(slot);
    const option = options.get(key);
    if (!option || option.resource !== resources[index]) return false;
    usage.set(key, (usage.get(key) || 0) + 1);
  }
  return [...usage].every(([key, count]) => {
    const option = options.get(key)!;
    return count <= (mode === "payment" ? option.amount : option.capacity);
  });
}

export function resourceLocationSelectionState({
  player,
  resources,
  mode,
  slots,
  location,
  incoming = [],
  singleLocation = false,
}: {
  player: PlayerView;
  resources: Resource[];
  mode: Mode;
  slots: Slot[];
  location: ResourceLocation;
  incoming?: ResourceLocation[];
  singleLocation?: boolean;
}) {
  const options = resourceLocations(player, mode === "payment" ? incoming : []);
  const option = options.find((candidate) => candidate.key === locationKey(location));
  const selected = slots.filter((slot) => slot && locationKey(slot) === locationKey(location)).length;
  if (!option) return { option: undefined, selected, limit: 0, required: 0, eligible: false, canAdd: false, canRemove: selected > 0 };
  const limit = mode === "payment" ? option.amount : option.capacity;
  const required = resources.filter((resource) => resource === option.resource).length;
  const eligible = required > 0
    && limit > 0
    && (!singleLocation || (resources.every((resource) => resource === option.resource) && limit >= resources.length));
  const openSlot = slots.some((slot, index) => !slot && resources[index] === option.resource);
  return {
    option,
    selected,
    limit,
    required,
    eligible,
    canAdd: eligible && (singleLocation ? selected !== resources.length : openSlot && selected < limit),
    canRemove: selected > 0,
  };
}

export function changeResourceSlots({
  player,
  resources,
  mode,
  slots,
  location,
  delta,
  incoming = [],
  singleLocation = false,
}: {
  player: PlayerView;
  resources: Resource[];
  mode: Mode;
  slots: Slot[];
  location: ResourceLocation;
  delta: -1 | 1;
  incoming?: ResourceLocation[];
  singleLocation?: boolean;
}): Slot[] {
  const state = resourceLocationSelectionState({ player, resources, mode, slots, location, incoming, singleLocation });
  if (delta < 0) {
    if (!state.canRemove) return slots;
    if (singleLocation) return Array<Slot>(resources.length).fill(null);
    const index = slots.map((slot) => slot ? locationKey(slot) : "").lastIndexOf(locationKey(location));
    if (index < 0) return slots;
    const next = [...slots];
    next[index] = null;
    return next;
  }
  if (!state.canAdd || !state.option) return slots;
  if (singleLocation) return Array<Slot>(resources.length).fill(location);
  const index = slots.findIndex((slot, slotIndex) => !slot && resources[slotIndex] === state.option!.resource);
  if (index < 0) return slots;
  const next = [...slots];
  next[index] = location;
  return next;
}

export function ResourceLocationPicker({ player, resources, mode, slots, onChange, allowUnstored = false, incoming = [], singleLocation = false, autoActivate = false }: {
  player: PlayerView;
  resources: Resource[];
  mode: Mode;
  slots: Slot[];
  onChange: (slots: Slot[]) => void;
  allowUnstored?: boolean;
  incoming?: ResourceLocation[];
  singleLocation?: boolean;
  autoActivate?: boolean;
}) {
  const options = resourceLocations(player, mode === "payment" ? incoming : []);
  const title = singleLocation ? "Sla alles op bij" : mode === "payment" ? "Betaal vanaf" : "Sla op bij";
  const picker = useResourceBoardPicker({
    title,
    player,
    resources,
    mode,
    slots,
    onChange,
    allowUnstored,
    incoming,
    singleLocation,
    autoActivate,
  });
  const selectedByLocation = options
    .map((option) => ({ option, count: slots.filter((slot) => slot && locationKey(slot) === option.key).length }))
    .filter(({ count }) => count > 0);
  const selected = slots.filter(Boolean).length;
  return (
    <fieldset className={`resource-location-picker visual-picker ${picker.active ? "active" : ""}`}>
      <legend>{title}</legend>
      <div className="resource-requirements" aria-label={`${selected} van ${resources.length} grondstoffen toegewezen`}>
        {[...new Set(resources)].map((resource) => {
          const required = resources.filter((candidate) => candidate === resource).length;
          const assigned = slots.filter((slot, index) => slot && resources[index] === resource).length;
          return (
            <span key={resource} style={{ ["--resource" as string]: resourceMeta[resource].color }} aria-label={`${assigned} van ${required} ${resourceMeta[resource].label} toegewezen`}>
              <ResourceIcon resource={resource} />
              <strong>{resourceMeta[resource].label}</strong>
              <b>{assigned}/{required}</b>
            </span>
          );
        })}
      </div>
      <div className="resource-source-summary">
        {selectedByLocation.length ? selectedByLocation.map(({ option, count }) => (
          <span key={option.key}>{count}× {option.label}</span>
        )) : <span>{allowUnstored ? "Nog niets opgeslagen; niet toegewezen grondstoffen gaan verloren." : "Nog geen landschap gekozen."}</span>}
      </div>
      <button type="button" className={picker.active ? "primary board-picker-active" : ""} onClick={picker.activate}>
        {picker.active ? "Bordselectie actief — gebruik − en +" : "Kies visueel op het bord"}
      </button>
      {selected ? (
        <button
          type="button"
          className="selection-clear"
          onClick={() => onChange(Array<Slot>(resources.length).fill(null))}
        >Selectie wissen</button>
      ) : null}
    </fieldset>
  );
}

export function ResourcePlanDialog({ player, request, confirmation, busy = false, onCancel, onConfirm }: {
  player: PlayerView;
  request: ResourcePlanRequest;
  confirmation?: ResourcePlanConfirmation;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (plan: ResourcePlan) => void;
}) {
  const initialStorage = allocateResourceSlots(player, request.storage, "storage");
  const [storage, setStorage] = useState<Slot[]>(initialStorage);
  const [payment, setPayment] = useState<Slot[]>(() => allocateResourceSlots(player, request.payment, "payment"));
  const valid = slotsAreValid(player, request.payment, payment, "payment", true)
    && slotsAreValid(player, request.storage, storage, "storage", request.requireStorage);
  const usesBoard = Boolean(request.payment.length || request.storage.length);
  return (
    <div className={`modal-layer priority-modal ${usesBoard ? `board-interaction-layer board-safe-dock-layer resource-board-dock-layer ${confirmation ? "build-confirmation-layer" : ""}` : "command-confirmation-layer"}`}>
      <section className={`modal small-modal resource-plan-modal board-interaction-dock ${usesBoard ? "board-safe-dock resource-board-dock" : ""}`} role="dialog" aria-modal={usesBoard ? "false" : "true"} aria-labelledby="resource-plan-title">
        <span className="eyebrow">{confirmation?.eyebrow || "Grondstoffen plaatsen"}</span>
        <h2 id="resource-plan-title">{confirmation?.title || request.title}</h2>
        <p>{confirmation?.description || <>Klik op <strong>−</strong> of <strong>+</strong> op de landschappen die je wilt draaien.</>}</p>
        {confirmation ? (
          <div className="command-cost-summary" aria-label={request.payment.length ? "Te betalen bouwkosten" : "Geen bouwkosten"}>
            <span>{request.payment.length ? "Kosten" : "Geen grondstoffen nodig"}</span>
            {request.payment.length ? <ResourceAmounts resources={request.payment} /> : null}
          </div>
        ) : null}
        {confirmation && usesBoard ? <p className="command-confirmation-hint">Pas de gekozen betaalkaarten zo nodig rechtstreeks op het bord aan met <strong>−</strong> en <strong>+</strong>.</p> : null}
        {request.payment.length ? <ResourceLocationPicker player={player} resources={request.payment} mode="payment" slots={payment} onChange={setPayment} autoActivate /> : null}
        {request.storage.length ? <ResourceLocationPicker player={player} resources={request.storage} mode="storage" slots={storage} onChange={setStorage} allowUnstored={!request.requireStorage} autoActivate={!request.payment.length} /> : null}
        <div className="modal-actions">
          <button onClick={onCancel}>Annuleren</button>
          <button className="primary" disabled={!valid || busy} onClick={() => onConfirm({ payment: payment.filter(Boolean) as ResourceLocation[], storage: storage.filter(Boolean) as ResourceLocation[] })}>{confirmation?.confirmLabel || "Bevestigen"}</button>
        </div>
      </section>
    </div>
  );
}

export function automaticPlan(player: PlayerView, request: ResourcePlanRequest): ResourcePlan {
  const storage = allocateResourceSlots(player, request.storage, "storage").filter(Boolean) as ResourceLocation[];
  return {
    payment: allocateResourceSlots(player, request.payment, "payment").filter(Boolean) as ResourceLocation[],
    storage,
  };
}
